import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LanguageService } from './language.service';
import { MobileSessionService } from './mobile-session.service';

type LoginDictionary = {
  title: { en: string; nl: string; fr: string };
  subtitle: { en: string; nl: string; fr: string };
  email: { en: string; nl: string; fr: string };
  password: { en: string; nl: string; fr: string };
  signIn: { en: string; nl: string; fr: string };
  invalid: { en: string; nl: string; fr: string };
  loading: { en: string; nl: string; fr: string };
  required: { en: string; nl: string; fr: string };
  goHome: { en: string; nl: string; fr: string };
  viewCatalog: { en: string; nl: string; fr: string };
};

const DICTIONARY: LoginDictionary = {
  title: {
    en: 'Mobile Dashboard Sign In',
    nl: 'Mobiel Dashboard Inloggen',
    fr: 'Connexion Tableau Mobile',
  },
  subtitle: {
    en: 'Sign in with email and password. Tier will be inferred from your account profile.',
    nl: 'Log in met e-mail en wachtwoord. Het niveau wordt afgeleid uit je accountprofiel.',
    fr: 'Connectez-vous avec e-mail et mot de passe. Le niveau sera deduit de votre profil.',
  },
  email: { en: 'Email', nl: 'E-mail', fr: 'E-mail' },
  password: { en: 'Password', nl: 'Wachtwoord', fr: 'Mot de passe' },
  signIn: { en: 'Sign In', nl: 'Inloggen', fr: 'Connexion' },
  invalid: {
    en: 'Invalid email or password.',
    nl: 'Ongeldig e-mailadres of wachtwoord.',
    fr: 'E-mail ou mot de passe invalide.',
  },
  loading: { en: 'Signing in...', nl: 'Bezig met inloggen...', fr: 'Connexion en cours...' },
  required: {
    en: 'Email and password are required.',
    nl: 'E-mail en wachtwoord zijn verplicht.',
    fr: 'E-mail et mot de passe sont obligatoires.',
  },
  goHome: { en: 'Home', nl: 'Start', fr: 'Accueil' },
  viewCatalog: { en: 'View Catalog', nl: 'Bekijk Catalogus', fr: 'Voir le catalogue' },
};

@Component({
  selector: 'app-mobile-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mobile-login.component.html',
  styleUrl: './mobile-login.component.css',
})
export class MobileLoginComponent {
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly sessionService = inject(MobileSessionService);

  public readonly language = this.languageService.language;

  public email = '';
  public password = '';
  public loading = false;
  public errorMessage = '';

  public t(key: keyof LoginDictionary): string {
    return DICTIONARY[key][this.language()];
  }

  public clearError(): void {
    if (!this.errorMessage) {
      return;
    }

    this.errorMessage = '';
  }

  public async submit(): Promise<void> {
    if (this.loading) {
      return;
    }

    this.errorMessage = '';
    const normalizedEmail = this.email.trim().toLowerCase();
    const normalizedPassword = this.password.trim();
    if (!normalizedEmail || !normalizedPassword) {
      this.errorMessage = this.t('required');
      return;
    }

    this.loading = true;
    const result = await this.sessionService.login(normalizedEmail, normalizedPassword);
    this.loading = false;

    if (!result.ok) {
      this.errorMessage = result.message || this.t('invalid');
      return;
    }

    void this.router.navigateByUrl('/mobile/dashboard');
  }

  public goHome(): void {
    void this.router.navigateByUrl('/');
  }

  public viewCatalog(): void {
    void this.router.navigateByUrl('/');
  }
}
